import { Grid } from '@/components/slides/SlideComponentSentenceMaker/Grid';
import { getDifficultyLabel, getRhetoricLabel, getWritingTechniqueLabel } from '@/components/slides/SlideComponentSentenceMaker/options';
import { Question, SlideComponentSentenceMaker } from '@/types/slide';
import { Box, Chip, Stack, Typography } from '@mui/material';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDuration } from '../../format';
import { ResultContainer, ResultContent, ResultTitle } from '../ResultCard';
import { ResultCardUnsupported } from './ResultCardUnsupported';
// Type guard for sentence maker - update when type is added to slide.tsx
function isSentenceMakerQuestion(question: Question): boolean {
    return question.type === 'sentenceMaker';
}

interface EvaluationCriteria {
    score: number;
    score_100: number;
    weighted_contribution: number;
    comment: string;
}

interface BinaryEvaluation {
    present: boolean;
    details: string;
}

interface SentenceMakerEvaluation {
    content: EvaluationCriteria;
    task_fulfillment: EvaluationCriteria;
    language: EvaluationCriteria;
    conventions: EvaluationCriteria;
    colloquial_usage: BinaryEvaluation;
    language_mixing: BinaryEvaluation;
}

interface SentenceMakerAssessmentResult {
    overall_score: number;
    overall_score_100: number;
    feedback: string;
    hint: string;
    evaluation: SentenceMakerEvaluation;
}

interface SentenceMakerAssessmentResponse {
    result: SentenceMakerAssessmentResult;
    answer: string;
}

interface SentenceMakerAnswer {
    data?: SentenceMakerAssessmentResponse;
    startTime: string;
    endTime: string;
}

const GRID_CELL_SIZE = 42; // Adjust as needed

export const ResultCardSentenceMaker: React.FC<{ question: Question }> = ({ question }) => {
    const { t } = useTranslation();
    const gridContainerRef = useRef<HTMLDivElement>(null);

    if (!isSentenceMakerQuestion(question)) {
        return <ResultCardUnsupported question={question} />;
    }

    const hasAnswer = question.answers && question.answers.length > 0;
    const questionData = question.content as unknown as SlideComponentSentenceMaker['content'];
    const answer = hasAnswer ? (question.answers[0] as unknown as SentenceMakerAnswer) : null;

    const data = answer?.data;
    const paresedSentenceMakerAnswer = data ? (JSON.parse(data as unknown as string) as unknown as SentenceMakerAssessmentResponse) : null;
    const sentenceMakerResult = paresedSentenceMakerAnswer?.result;
    const studentSentence = paresedSentenceMakerAnswer?.answer;
    const difficultyLabel = questionData?.difficulty ? getDifficultyLabel(questionData.difficulty, t) || questionData.difficulty : 'N/A';
    const writingTechniqueLabel = questionData?.writingTechnique ? getWritingTechniqueLabel(questionData.writingTechnique, t) || questionData.writingTechnique : 'N/A';
    const rhetoricLabel = questionData?.rhetoric ? getRhetoricLabel(questionData.rhetoric, t) || questionData.rhetoric : 'N/A';

    const timeSpent = hasAnswer
        ? formatDuration(
              question.answers.reduce((total, ans) => {
                  const start = new Date(ans.startTime).getTime();
                  const end = new Date(ans.endTime).getTime();
                  return total + (end - start) / 1000;
              }, 0),
          )
        : '00:00:00';

    return (
        <ResultContainer>
            <Stack spacing={2} sx={{ p: 2 }}>
                {/* Title */}
                <ResultTitle variant='h5'>{t('slide.components.sentenceMaker.title')}</ResultTitle>

                {/* Metadata Row 1 */}
                <Stack direction='row' spacing={4} flexWrap='wrap'>
                    <Typography variant='body2' color='text.secondary'>
                        <strong>{t('slide.components.sentenceMaker.difficultyLevel')}:</strong> {difficultyLabel}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        <strong>{t('slide.components.sentenceMaker.language.title')}:</strong>{' '}
                        {(questionData?.language === 'traditional' ? t('slide.components.sentenceMaker.language.traditional') : t('slide.components.sentenceMaker.language.simplified')) ||
                            t('slide.components.sentenceMaker.language.traditional') ||
                            'N/A'}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        {/* use chip to represent every keyword */}
                        {questionData?.keywords && questionData?.keywords?.length > 0 ? (
                            <>
                                <strong>{t('slide.components.sentenceMaker.keywords.label')}:</strong>{' '}
                                {questionData.keywords.map((kw, index) => (
                                    <Chip
                                        key={index}
                                        component='span'
                                        sx={{
                                            borderRadius: 1,
                                            fontSize: '0.75rem',
                                            backgroundColor: (theme) => (theme.vars || theme).palette.grey[200],
                                            mr: 0.5,
                                        }}
                                        label={kw}
                                    />
                                ))}
                            </>
                        ) : (
                            <>
                                <strong>{t('slide.components.sentenceMaker.keywords.label')}:</strong> N/A
                            </>
                        )}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant='body2' color='text.secondary'>
                        <strong>{t('result.label.timeSpent')}:</strong> {timeSpent}
                    </Typography>
                </Stack>

                {/* Metadata Row 2 */}
                <Stack direction='row' spacing={4} flexWrap='wrap'>
                    <Typography variant='body2' color='text.secondary'>
                        <strong>{t('slide.components.sentenceMaker.sentencePattern.label')}:</strong> {questionData?.sentencePattern || 'N/A'}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        <strong>{t('slide.components.sentenceMaker.theme.label')}:</strong> {questionData?.theme || 'N/A'}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        <strong>{t('slide.components.sentenceMaker.writingTechnique.title')}:</strong> {writingTechniqueLabel}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        <strong> {t('slide.components.sentenceMaker.rhetoric.title')}:</strong> {rhetoricLabel}
                    </Typography>
                </Stack>

                <ResultContent>
                    <Stack direction='row' spacing={2} justifyContent='space-between' alignItems='stretch'>
                        {/* Grid Section */}
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: 1, p: 1 }}>
                            <Box
                                ref={gridContainerRef}
                                sx={{
                                    width: `${GRID_CELL_SIZE * (questionData?.size?.cols || 10)}px`,
                                    height: `${GRID_CELL_SIZE * (questionData?.size?.rows || 4)}px`,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    p: 1,
                                }}
                            >
                                <Grid
                                    cols={questionData?.size?.cols || 10}
                                    rows={questionData?.size?.rows || 4}
                                    language={questionData?.language || 'traditional'}
                                    text={studentSentence || ''}
                                    gridSizeY={GRID_CELL_SIZE}
                                    keywords={questionData?.keywords || []}
                                />
                                {/* </Box> */}
                            </Box>
                        </Box>

                        {/* Scoring Section */}
                        <Box
                            sx={{
                                width: '480px',
                                border: '1px solid rgba(0, 0, 0, 0.12)',
                                borderRadius: 1,
                                overflow: 'hidden',
                            }}
                        >
                            {/* Header - Overall Score */}
                            <Box
                                sx={{
                                    px: 2,
                                    py: 1,
                                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                                }}
                            >
                                <Typography variant='h6' fontWeight={500}>
                                    {t('assessment.sentenceMaker.overallScore')}
                                </Typography>
                                <Typography
                                    variant='h4'
                                    sx={{
                                        fontWeight: 'bold',
                                        color: 'primary.dark',
                                        letterSpacing: '0.25px',
                                    }}
                                >
                                    {sentenceMakerResult?.overall_score_100 || 0}
                                </Typography>
                            </Box>

                            {/* Content Score */}
                            <Box
                                sx={{
                                    px: 2,
                                    py: 1,
                                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                                    display: 'flex',
                                    gap: 1,
                                }}
                            >
                                <Box sx={{ width: 110, flexShrink: 0 }}>
                                    <Typography variant='body2' color='text.primary'>
                                        {t('assessment.sentenceMaker.content')}
                                    </Typography>
                                    <Typography variant='h6' sx={{ color: 'primary.dark' }}>
                                        {sentenceMakerResult?.evaluation?.content?.score_100 || 0}
                                    </Typography>
                                </Box>
                                <Typography variant='body2' color='text.secondary' sx={{ flex: 1 }}>
                                    {sentenceMakerResult?.evaluation?.content?.comment || ''}
                                </Typography>
                            </Box>

                            {/* Task Fulfilment Score */}
                            <Box
                                sx={{
                                    px: 2,
                                    py: 1,
                                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                                    display: 'flex',
                                    gap: 1,
                                }}
                            >
                                <Box sx={{ width: 110, flexShrink: 0 }}>
                                    <Typography variant='body2' color='text.primary'>
                                        {t('assessment.sentenceMaker.taskFulfillment')}
                                    </Typography>
                                    <Typography variant='h6' sx={{ color: 'primary.dark' }}>
                                        {sentenceMakerResult?.evaluation?.task_fulfillment?.score_100 || 0}
                                    </Typography>
                                </Box>
                                <Typography variant='body2' color='text.secondary' sx={{ flex: 1 }}>
                                    {sentenceMakerResult?.evaluation?.task_fulfillment?.comment || ''}
                                </Typography>
                            </Box>

                            {/* Language Score */}
                            <Box
                                sx={{
                                    px: 2,
                                    py: 1,
                                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                                    display: 'flex',
                                    gap: 1,
                                }}
                            >
                                <Box sx={{ width: 110, flexShrink: 0 }}>
                                    <Typography variant='body2' color='text.primary'>
                                        {t('assessment.sentenceMaker.language')}
                                    </Typography>
                                    <Typography variant='h6' sx={{ color: 'primary.dark' }}>
                                        {sentenceMakerResult?.evaluation?.language?.score_100 || 0}
                                    </Typography>
                                </Box>
                                <Typography variant='body2' color='text.secondary' sx={{ flex: 1 }}>
                                    {sentenceMakerResult?.evaluation?.language?.comment || ''}
                                </Typography>
                            </Box>

                            {/* Conventions Score */}
                            <Box
                                sx={{
                                    px: 2,
                                    py: 1,
                                    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                                    display: 'flex',
                                    gap: 1,
                                }}
                            >
                                <Box sx={{ width: 110, flexShrink: 0 }}>
                                    <Typography variant='body2' color='text.primary'>
                                        {t('assessment.sentenceMaker.conventions')}
                                    </Typography>
                                    <Typography variant='h6' sx={{ color: 'primary.dark' }}>
                                        {sentenceMakerResult?.evaluation?.conventions?.score_100 || 0}
                                    </Typography>
                                </Box>
                                <Typography variant='body2' color='text.secondary' sx={{ flex: 1 }}>
                                    {sentenceMakerResult?.evaluation?.conventions?.comment || ''}
                                </Typography>
                            </Box>
                        </Box>
                    </Stack>
                </ResultContent>
            </Stack>
        </ResultContainer>
    );
};
